import { XYLocation } from "@nens/deck-gl";
import * as api from "@nens/threedi/openapi-client";

import { getV3BetaApi } from "../apiConfiguration";
import { AppState, Thunk } from "../App";
import { remindUserToPressPlay, sendMessage } from "../state/mainGui";

import { BACK_TO_START_SCREEN, BackToStartScreen } from "./appState";
import { RequestStatus } from "./requestStatus";
import { getSimulation, getSimulationStatus } from "./simulation";

export type PolygonStatus = "SELECT_OR_NEW" | "DRAWING_MODE";

export type EditableRaster = "DEM" | "INTERCEPTION" | "INFILTRATION";
export type MaybePoints = null | XYLocation[];

export type NewValue = string;

interface PolygonEdit {
  status: PolygonStatus;
  points: MaybePoints;
  newValue: NewValue;
  editableRaster: EditableRaster;
  apiRequestStatus: RequestStatus;
  failedMessage: string;
}

const INITIAL_STATE: PolygonEdit = {
  status: "SELECT_OR_NEW",
  points: null,
  newValue: "0",
  editableRaster: "DEM",
  apiRequestStatus: "NOT_SENT",
  failedMessage: "",
};

const SET_STATUS = "leveeEdit/SET_STATUS";
const SET_API_REQUEST_STATUS = "leveeEdit/SET_API_REQUEST_STATUS";
const SET_POINTS = "leveeEdit/SET_POINTS";
const REMOVE_LAST_POINT = "leveeEdit/REMOVE_LAST_POINT";
const SET_NEW_VALUE = "leveeEdit/SET_NEW_VALUE";
const SET_EDITABLE_RASTER = "leveeEdit/SET_EDITABLE_RASTER";

export const getStatus = (state: AppState) => state.leveeEdit["status"];
export const getApiRequestStatus = (state: AppState) => state.leveeEdit["apiRequestStatus"];
export const getPoints = (state: AppState): MaybePoints => state.leveeEdit["points"];
export const getNewValue = (state: AppState) => state.leveeEdit["newValue"];
export const getEditableRaster = (state: AppState) => state.leveeEdit["editableRaster"];
export const getIsReadyToFinalizeConfirm = (state: AppState) => {
  const points = getPoints(state);
  const apiState = getApiRequestStatus(state);
  return !!(apiState !== "SENT" && points && points.length > 1);
};
export const getIsUndoLastPointRelevant = (state: AppState): boolean =>
  !!(state.leveeEdit["points"] && (state.leveeEdit["points"] as XYLocation[]).length > 0);

interface SetStatus {
  type: typeof SET_STATUS;
  status: PolygonStatus;
}
interface SetApiRequestStatus {
  type: typeof SET_API_REQUEST_STATUS;
  failedMessage: string;
  apiRequestStatus: RequestStatus;
}
interface SetPoints {
  type: typeof SET_POINTS;
  points: MaybePoints;
}
interface RemoveLastPoint {
  type: typeof REMOVE_LAST_POINT;
}
interface SetNewValue {
  type: typeof SET_NEW_VALUE;
  newValue: NewValue;
}
interface SetEditableRaster {
  type: typeof SET_EDITABLE_RASTER;
  editableRaster: EditableRaster;
}

type PolygonEditAction =
  | SetStatus
  | SetApiRequestStatus
  | SetPoints
  | RemoveLastPoint
  | SetNewValue
  | SetEditableRaster
  | BackToStartScreen;

export const setStatusAction = (status: PolygonStatus): PolygonEditAction => {
  return {
    type: SET_STATUS,
    status: status,
  };
};
export const setApiRequestStatusAction = (
  failedMessage: string,
  apiRequestStatus: RequestStatus
): PolygonEditAction => {
  return {
    type: SET_API_REQUEST_STATUS,
    apiRequestStatus: apiRequestStatus,
    failedMessage: failedMessage,
  };
};
export const setPointsAction = (points: MaybePoints): PolygonEditAction => {
  return {
    type: SET_POINTS,
    points: points,
  };
};
export const removeLastPointAction = (): PolygonEditAction => {
  return {
    type: REMOVE_LAST_POINT,
  };
};

export const setNewValueAction = (newValue: NewValue): PolygonEditAction => {
  return {
    type: SET_NEW_VALUE,
    newValue: newValue,
  };
};

export const pointsToGeoJsonPolygonString = (linePoints: XYLocation[]) => {
  const linePointsArrayAsStrings =
    linePoints &&
    linePoints.map((item) => {
      return "[" + item[0] + ", " + item[1] + "]";
    });
  const linePointsArrayAsString = linePointsArrayAsStrings && linePointsArrayAsStrings.join(",");
  const geojsonPolygonString =
    linePointsArrayAsString &&
    '{"type":"LineString","coordinates":[' + linePointsArrayAsString + "]}";
  return geojsonPolygonString;
};

export const submitLeveeEdit =
  (errorMessage: string | JSX.Element): Thunk =>
  (dispatch, getState) => {
    const state = getState();
    const linePoints = getPoints(state);
    if (!linePoints) {
      return;
    }
    const geojsonPolygonString = pointsToGeoJsonPolygonString(linePoints);
    const simulation = getSimulation(state);
    const simulationStatus = getSimulationStatus(state);
    const newValue = getNewValue(state);

    const v3BetaApi = getV3BetaApi();
    if (geojsonPolygonString === null) {
      // todo error handling to user needed? Or can this never happen
      console.error(
        "No linePoints therefore not possible to send linePoints to api. linePoints of: ",
        linePoints
      );
      return;
    } else if (simulation === null) {
      // todo error handling to user needed? Or can this never happen
      console.error(
        "No simulation therefore not possible to send linePoints to api. simulation of: ",
        simulation
      );
      return;
    }
    dispatch(setApiRequestStatusAction("", "SENT"));
    const leveeSimulationId = simulation.id;
    if (leveeSimulationId === undefined) return;
    const simulationsEventsRasterEditsCreateRequest: api.SimulationsEventsObstacleEditsCreateRequest =
      {
        simulationPk: leveeSimulationId,
        data: {
          offset: simulationStatus.time, //number;
          value: parseFloat(newValue + ""), // number;
          linestring: JSON.parse(geojsonPolygonString),
        },
      };

    v3BetaApi
      .simulationsEventsObstacleEditsCreate(simulationsEventsRasterEditsCreateRequest)
      .then((_response) => {
        dispatch(setApiRequestStatusAction("", "SUCCESS"));
        dispatch(setStatusAction("SELECT_OR_NEW"));
      })
      .catch((error) => {
        dispatch(setApiRequestStatusAction(error, "FAILED"));
        console.error("raster edit failed with following error", error);
        dispatch(sendMessage(errorMessage, 25000, "error"));
        dispatch(setStatusAction("SELECT_OR_NEW"));
      });
    dispatch(setStatusAction("SELECT_OR_NEW"));
    dispatch(remindUserToPressPlay());
  };

export default function reducer(state = INITIAL_STATE, action: PolygonEditAction) {
  switch (action.type) {
    case BACK_TO_START_SCREEN:
      return INITIAL_STATE;
    case SET_STATUS: {
      const { status } = action;
      if (status !== state.status) {
        return {
          ...state,
          status: status,
          points: status === "SELECT_OR_NEW" ? null : state.points,
        };
      } else {
        return state;
      }
    }
    case SET_POINTS: {
      const { points } = action;
      const status = state.status;

      return {
        ...state,
        status,
        points,
      };
    }
    case REMOVE_LAST_POINT: {
      const oldPoints = state.points;
      if (oldPoints && oldPoints.length > 1) {
        return {
          ...state,
          points: oldPoints.slice(0, -1),
        };
      } else {
        const status = "SELECT_OR_NEW";
        const points = null;
        return {
          ...state,
          status,
          points,
        };
      }
    }
    case SET_NEW_VALUE: {
      const { newValue } = action;
      return {
        ...state,
        newValue,
      };
    }
    case SET_EDITABLE_RASTER: {
      const { editableRaster } = action;
      return {
        ...state,
        editableRaster,
      };
    }
    default:
      return state;
  }
}
